/** @jsxImportSource @emotion/react */
import React, { FC } from 'react';
import { FormikErrors } from 'formik';
import { Selectable, TextFields } from '@zip/business-components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import {
  EcommercePlatformProps,
  StepComponentProps,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { Validators } from 'utils';
import { StepIndex } from '../signUpConfig';
import * as styles from './SignUpForm.styles';
import {
  SignUpRadioStepConfig,
  SignUpRadioStepForm,
  useSignUpRadioStep,
} from './useSignUpRadioStep.module';

const OTHER_OPTION = 'Other';
const OTHER_VALUE_MAX_LENGTH = 50;

const validateOtherValue = (
  values: EcommercePlatformProps
): FormikErrors<EcommercePlatformProps> => {
  const errors: FormikErrors<EcommercePlatformProps> = {};

  if (values.platform !== OTHER_OPTION) {
    return errors;
  }

  if (!values.otherValue?.trim()) {
    errors.otherValue = 'Please specify your e-commerce platform';
  } else if (values.otherValue.length > OTHER_VALUE_MAX_LENGTH) {
    errors.otherValue = `Must be ${OTHER_VALUE_MAX_LENGTH} characters or less`;
  }

  return errors;
};

const config: SignUpRadioStepConfig<EcommercePlatformProps> = {
  stepIndex: StepIndex.EcommercePlatform,
  selectionField: 'platform',
  requiredSelectionMessage: 'Please select an option',
  initialValues: {
    platform: '',
    otherValue: '',
  },
  validate: validateOtherValue,
};

export const EcommercePlatformStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  serverErrors,
}) => {
  const { formOptions } = useMerchantApplicationContext();
  const options: Selectable[] = (formOptions.ECommercePlatform ?? []).map(
    (answer) => ({ label: answer, value: answer })
  );
  const step = useSignUpRadioStep(config, options, {
    provideSubmit,
    serverErrors,
  });
  const isOtherSelected = step.formik.values.platform === OTHER_OPTION;

  React.useEffect(() => {
    if (!isOtherSelected && step.formik.values.otherValue) {
      step.formik.setFieldValue('otherValue', '');
    }
  }, [isOtherSelected, step.formik]);

  return (
    <>
      <SignUpRadioStepForm {...step} />
      {isOtherSelected && (
        <TextFields.Outlined
          type="text"
          name="otherValue"
          label="Please specify your e-commerce platform"
          css={[styles.textfield, styles.textfieldNoMargin]}
          fullWidth
          value={step.formik.values.otherValue ?? ''}
          onChange={step.formik.handleChange}
          onBlur={step.formik.handleBlur}
          inputProps={{ maxLength: OTHER_VALUE_MAX_LENGTH }}
          error={step.submitAttempted && Boolean(step.formik.errors.otherValue)}
          helperText={Validators.renderFieldError(
            step.formik.touched.otherValue,
            step.formik.errors.otherValue,
            step.submitAttempted
          )}
        />
      )}
    </>
  );
};

export default EcommercePlatformStepModule;
